<div *ngIf="documents$ | async as documents">
    <div *ngIf="documents.length">
        <div class="flex flex-row items-center justify-between">
            <!-- toLocaleString will give us commas. e.g. 12,000 -->
            <h2>Documents <span class="!font-light !text-gray-400">({{
                selection?.selected.length
                    ? selection.selected.length.toLocaleString()
                    : tableDataSource.filteredData.length.toLocaleString()
            }})</span></h2>


            <!-- Top-right search and file upload button. -->
            <div class="flex items-center">
                <!-- Search box -->
                <mat-form-field appearance="outline" color="accent" class="lendio-density-2" *ngIf="borrowerId">
                    <mat-icon matPrefix color="accent">search</mat-icon>
                    <input matInput
                        (keyup)="applyFilter($event)"
                        placeholder="Search"
                        data-cy="document-search"
                        #search
                    >
                </mat-form-field>
                <!--
                    Note: The material theme defines `densities` for different input
                    sizes (e.g. lendio-density-2). Buttons don't yet have this concept
                    and we can easily achieve it with tailwind utility classes
                    for now (!h-8 !min-h-0).
                -->
                <button mat-flat-button color="primary"
                    class="ml-2 !h-8 !min-h-0"
                    *ngIf="borrowerId && _hasModifyAccess(business$ | async)"
                    (click)="openUploadDialog()"
                >Upload</button>
            </div>

        </div>

        <!-- Actions buttons -->
        <ng-container>
            <div class="flex space-x-2 mb-4">
                <button mat-stroked-button
                    class="!h-8 !min-h-0"
                    color="accent"
                    (click)="downloadDocs()"
                    [disabled]="noneSelected() || uncleanDocsSelected()">
                    Download
                </button>
                <button mat-stroked-button
                    class="!h-8 !min-h-0"
                    color="accent"
                    *ngIf="showZipAll()"
                    (click)="zipAll()">
                    Zip All
                </button>
                <button mat-stroked-button
                    class="!h-8 !min-h-0"
                    *ngIf="borrowerId"
                    color="accent"
                    [disabled]="noneSelected()"
                    (click)="deleteDocs($event)">
                    Delete
                </button>
            </div>
        </ng-container>

        <!-- Table -->
        <table
            mat-table
            [dataSource]="tableDataSource$ | async"
            matSort
            matSortActive="id"
            matSortDirection="desc"
            #table="matTable"
        >
            <!-- Checkbox -->
            <ng-container matColumnDef="checkbox">
                <th mat-header-cell *matHeaderCellDef>
                    <mat-checkbox
                        (change)="$event ? toggleAllRows() : null"
                        [checked]="
                            (selection.hasValue() && isAllSelected()) ||
                            (selection.hasValue() && !isAllSelected())
                        "
                        [indeterminate]="selection.hasValue() && !isAllSelected()"
                        color="primary"
                    >
                    </mat-checkbox>
                </th>
                <td mat-cell class="!w-8" *matCellDef="let document">
                    <mat-checkbox
                        (click)="$event.stopPropagation()"
                        (change)="$event ? selection.toggle(document) : null"
                        [checked]="selection.isSelected(document)"
                        [disabled]="borrowerId == null && document.scanStatus !== 'CLEAN'"
                    >
                    </mat-checkbox>
                </td>
            </ng-container>

            <!-- Doc Title -->
            <ng-container matColumnDef="title">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>Title</th>
                <td mat-cell *matCellDef="let document">
                    <ng-container *ngIf="document.scanStatus !== 'CLEAN'; else linkTitle" >
                       <div class="text-slate-400">{{ document.title }}</div>
                      </ng-container>

                      <ng-template #linkTitle>
                        <button class="cursor-pointer link"
                        target="_blank"
                        (click)="viewDocument(document.id)">
                        {{ document.title}}
                        </button>
                      </ng-template>


                </td>
            </ng-container>

            <!-- Doc Type -->
            <ng-container matColumnDef="category">
                <th mat-header-cell *matHeaderCellDef mat-sort-header="category">Type</th>
                <td mat-cell *matCellDef="let document">
                    {{ getDocType(document.category) }}
                </td>
            </ng-container>

            <!-- For month -->
            <ng-container matColumnDef="monthsString" *ngIf="borrowerId">
                <th mat-header-cell *matHeaderCellDef mat-sort-header="months[0]">For month</th>
                <td mat-cell *matCellDef="let document">
                    {{ formatMonthYear(document.months[0]) }}
                </td>
            </ng-container>

            <!-- Uploaded -->
            <ng-container matColumnDef="created" *ngIf="borrowerId">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>Uploaded</th>
                <td mat-cell *matCellDef="let document" class="!py-1">
                    <div>
                        {{ getDateAgo(document.createdTimestamp) }}
                    </div>
                    <div class="!font-thin text-xs text-slate-400 tracking-tight">
                        {{ getDateString(document.createdTimestamp) }}
                    </div>
                </td>
            </ng-container>

            <!-- Uploaded by-->
            <ng-container matColumnDef="uploadedByName" *ngIf="borrowerId">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>Uploaded by</th>
                <td mat-cell *matCellDef="let document">
                    {{ document.uploadedByName }}
                </td>
            </ng-container>

            <!-- Scan -->
            <ng-container matColumnDef="scanStatus">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>Scan</th>
                <td mat-cell *matCellDef="let document">
                    <mat-spinner *ngIf="!document.scanStatus || document.scanStatus === 'PENDING'"
                        matTooltip="Scan in progress... This may take a few minutes."
                        [diameter]="20">
                    </mat-spinner>
                    <mat-icon *ngIf="document.scanStatus === 'CLEAN'"
                        class="material-symbols-outlined green-check"
                        fontSet="material-symbols-outlined"
                        matTooltip="Scan successfully completed">
                        done
                    </mat-icon>
                    <mat-icon *ngIf="document.scanStatus === 'INFECTED'"
                        class="material-symbols-outlined red-close"
                        fontSet="material-symbols-outlined"
                        matTooltip="Document scan failed. Try uploading a new file.">
                        close
                    </mat-icon>
                </td>
            </ng-container>

            <!-- Doc row menu button -->
            <ng-container matColumnDef="menu" *ngIf="borrowerId">
                <th mat-header-cell *matHeaderCellDef></th>
                <td mat-cell *matCellDef="let document">
                    <button mat-icon-button
                        class="lendio-table-icon-button"
                        color="accent"
                        [matMenuTriggerFor]="docMenu"
                        data-cy="doc-row-menu">
                        <mat-icon>more_horiz</mat-icon>
                    </button>

                    <mat-menu #docMenu="matMenu"
                            [hasBackdrop]="true"
                            class="cy-menu-basic">
                        <button mat-menu-item (click)="openEditDialog(document)">
                            Edit details
                        </button>
                        <button mat-menu-item (click)="downloadDocs(document)" [disabled]="document.scanStatus !== 'CLEAN'">
                            Download
                        </button>
                        <button mat-menu-item (click)="deleteDocs($event, document)">
                            Delete
                        </button>
                    </mat-menu>
                </td>
            </ng-container>
            <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
        </table>

        <lendio-paginator [pageSizeOptions]="[25, 50, 100]"></lendio-paginator>
    </div>

    <!-- No documents -->
    <div *ngIf="documents.length == 0">
        <div class="flex flex-row items-center justify-between">
            <!-- toLocaleString will give us commas. e.g. 12,000 -->
            <h2>Documents <span class="!font-light !text-gray-400">({{
                selection?.selected.length
                    ? selection.selected.length.toLocaleString()
                    : tableDataSource.filteredData.length.toLocaleString()
            }})</span></h2>


            <!-- Top-right search and file upload button. -->
            <div class="flex items-center">
                <!-- Search box -->
                <mat-form-field appearance="outline" color="accent" class="lendio-density-2" *ngIf="borrowerId">
                    <mat-icon matPrefix color="accent">search</mat-icon>
                    <input matInput
                        (keyup)="applyFilter($event)"
                        placeholder="Search"
                        data-cy="document-search"
                        #search
                    >
                </mat-form-field>
                <button mat-flat-button color="primary"
                    class="ml-2 !h-8 !min-h-0"
                    *ngIf="borrowerId && _hasModifyAccess(business$ | async)"
                    (click)="openUploadDialog()"
                >Upload</button>
            </div>
        </div>
        <div class="my-4 m-auto">
            <img class="m-auto pb-4" src="/assets/images/FAQ_Illo 1.svg" />
            <div class="text-center font-normal text-gray-400">
                There are no documents.
                <span *ngIf="borrowerId">
                    <br>
                Click "Upload" to get started.
                </span>
            </div>
        </div>
    </div>
</div>
